<template>
    <div>
        <!-- One tour at a time, shown with the full feature treatment so each
             slide fills the rail. Keyed by id so swapping remounts the feature
             (and re-registers an impression for the newly shown tour). -->
        <transition name="rail-fade" mode="out-in">
            <div :key="current.id">
                <related-tour-feature :tour="current" :url="routeFor(current)"
                    @click="emit('view', current, index + 1)"
                    @impression="emit('impression', current, index + 1)" />
            </div>
        </transition>

        <!-- Carousel controls: grouped prev/next with a position counter. Only
             shown when there's more than one tour to move through. On lg the bar
             sticks to the viewport bottom so it stays reachable when the pinned
             rail is taller than the viewport; the negative inset bleeds the
             backdrop out to the rail's lg:p-5 padding. -->
        <div v-if="tours.length > 1"
            class="mt-3 flex items-center justify-between gap-2 lg:sticky lg:bottom-0 lg:z-10 lg:-mx-5 lg:-mb-5 lg:px-5 lg:py-3 lg:border-t lg:border-neutral-200 lg:bg-white/90 lg:backdrop-blur">
            <span class="text-xs font-medium tracking-wide text-neutral-500">
                {{ index + 1 }} / {{ tours.length }}
            </span>
            <p-button-group>
                <p-button severity="secondary" outlined size="small" icon="pi pi-chevron-left"
                    aria-label="Previous tour" @click="prev" />
                <p-button severity="secondary" outlined size="small" icon="pi pi-chevron-right"
                    aria-label="Next tour" @click="next" />
            </p-button-group>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Tour } from "@/types";
import { Button as PButton, ButtonGroup as PButtonGroup } from "primevue";
import RelatedTourFeature from "@/Components/Tour/Item/RelatedTourFeature.vue";

const emit = defineEmits<{
    view: [tour: Tour, position: number];
    impression: [tour: Tour, position: number];
}>();

const props = defineProps<{
    tours: Tour[];
    // Reseller-aware tour URL builder, supplied by the parent Tours component.
    routeFor: (tour: Tour) => string;
}>();

const index = ref(0);

const current = computed<Tour>(() => props.tours[index.value]);

// Wrap around in both directions so the control never dead-ends.
const next = () => (index.value = (index.value + 1) % props.tours.length);
const prev = () => (index.value = (index.value - 1 + props.tours.length) % props.tours.length);
</script>

<style scoped>
.rail-fade-enter-active,
.rail-fade-leave-active {
    transition: opacity 0.2s ease;
}

.rail-fade-enter-from,
.rail-fade-leave-to {
    opacity: 0;
}
</style>
